import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface KeyboardNavigationOptions {
  onEscape?: () => void;
  onEnter?: () => void;
  enableGlobalShortcuts?: boolean;
}

export const useKeyboardNavigation = (options: KeyboardNavigationOptions = {}) => {
  const navigate = useNavigate();
  const { onEscape, onEnter, enableGlobalShortcuts = true } = options;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Skip if user is typing in an input
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target instanceof HTMLElement && e.target.isContentEditable)
    ) {
      return;
    }

    // Escape - go back or trigger callback
    if (e.key === 'Escape') {
      if (onEscape) {
        onEscape();
      } else {
        navigate(-1);
      }
      return;
    }

    // Enter callback
    if (e.key === 'Enter' && onEnter) {
      onEnter();
      return;
    }

    // Global shortcuts (when enabled)
    if (enableGlobalShortcuts) {
      // H - go to hallway
      if (e.key === 'h' && !e.ctrlKey && !e.metaKey) {
        navigate('/hallway');
        return;
      }

      // S - go to stats
      if (e.key === 's' && !e.ctrlKey && !e.metaKey) {
        navigate('/stats');
        return;
      }

      // A - go to achievements
      if (e.key === 'a' && !e.ctrlKey && !e.metaKey) {
        navigate('/achievements');
        return;
      }

      // ? - go to random room (same as shuffle button)
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        const event = new CustomEvent('randomRoom');
        window.dispatchEvent(event);
        return;
      }

      // Ctrl+/ or Cmd+/ - focus search
      if (e.key === '/' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="earch"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
        return;
      }
    }
  }, [navigate, onEscape, onEnter, enableGlobalShortcuts]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    // Focus trap helpers
    trapFocus: (containerRef: React.RefObject<HTMLElement>) => {
      if (!containerRef.current) return;
      
      const focusableElements = containerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  };
};
